import React from 'react';
import { IconBrain, IconBook, IconClock, IconSchool } from '@tabler/icons-react';

const LoadingPassages = () => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 p-6">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Progress bar animation */}
        <div className="h-1 bg-gray-100 w-full">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 animate-pulse-width"></div>
        </div>
        
        <div className="p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Loading Your Test</h2>
          
          {/* Quiz icon animation container */}
          <div className="relative h-32 mb-8">
            {/* Animated icons */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="quiz-icon-orbit">
                <IconBook className="text-indigo-500 h-8 w-8" />
                <IconClock className="text-purple-500 h-8 w-8" />
                <IconBrain className="text-teal-500 h-8 w-8" />
                <IconSchool className="text-amber-500 h-8 w-8" />
              </div>
              
              {/* Center pulsing circle */}
              <div className="absolute h-16 w-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full animate-pulse flex items-center justify-center">
                <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center">
                  <div className="h-8 w-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Loading text with typewriter effect */}
          <div className="text-center mb-6">
            <p className="text-gray-600 typewriter-text">Loading passages...</p>
          </div>
          
          {/* Paper sheets stacking animation */}
          <div className="relative h-20 mx-auto w-40">
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform rotate-3 top-3 left-2 animate-stack-1"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform -rotate-2 top-2 left-0 animate-stack-2"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full transform rotate-1 top-1 -left-2 animate-stack-3"></div>
            <div className="paper-stack p-4 bg-white border border-gray-200 rounded shadow absolute w-full h-full top-0 left-0 z-10">
              <div className="h-2 w-12 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-2 w-24 bg-gray-200 rounded mb-2 animate-pulse delay-100"></div>
              <div className="h-2 w-16 bg-gray-200 rounded animate-pulse delay-200"></div>
            </div>
          </div>
          
          {/* Loading tips */}
          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm fade-in-up">Get ready to focus and do your best!</p>
          </div>
        </div>
      </div>
      
      {/* CSS animations */}
      <style jsx="true">{`
        @keyframes pulse-width {
          0% { width: 10%; }
          50% { width: 70%; }
          100% { width: 10%; }
        }
        
        @keyframes orbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes stack-hover-1 {
          0%, 100% { transform: rotate(3deg) translateY(0); }
          50% { transform: rotate(3deg) translateY(-2px); }
        }
        
        @keyframes stack-hover-2 {
          0%, 100% { transform: rotate(-2deg) translateY(0); }
          50% { transform: rotate(-2deg) translateY(-3px); }
        }
        
        @keyframes stack-hover-3 {
          0%, 100% { transform: rotate(1deg) translateY(0); }
          50% { transform: rotate(1deg) translateY(-1px); }
        }
        
        @keyframes typewriter {
          from { width: 0; }
          to { width: 160px; }
        }

        @keyframes fade-in-up {
          from { 
            opacity: 0;
            transform: translateY(10px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-pulse-width {
          animation: pulse-width 2s infinite ease-in-out;
        }
        
        .quiz-icon-orbit {
          position: relative;
          width: 100px;
          height: 100px;
          animation: orbit 10s infinite linear;
        }
        
        .quiz-icon-orbit > svg {
          position: absolute;
          transform-origin: center;
        }
        
        .quiz-icon-orbit > svg:nth-child(1) {
          top: -20px;
          left: calc(50% - 16px);
        }
        
        .quiz-icon-orbit > svg:nth-child(2) {
          top: calc(50% - 16px);
          right: -20px;
        }
        
        .quiz-icon-orbit > svg:nth-child(3) {
          bottom: -20px;
          left: calc(50% - 16px);
        }
        
        .quiz-icon-orbit > svg:nth-child(4) {
          top: calc(50% - 16px);
          left: -20px;
        }
        
        .animate-stack-1 {
          animation: stack-hover-1 3s infinite ease-in-out;
        }
        
        .animate-stack-2 {
          animation: stack-hover-2 4s infinite ease-in-out;
        }
        
        .animate-stack-3 {
          animation: stack-hover-3 3.5s infinite ease-in-out;
        }
        
        .typewriter-text {
          overflow: hidden;
          border-right: 2px solid #6366f1;
          white-space: nowrap;
          margin: 0 auto;
          animation: 
            typewriter 3s steps(20) infinite alternate,
            blink-caret 0.75s step-end infinite;
        }

        .fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          animation-delay: 1s;
          opacity: 0;
        }
        
        @keyframes blink-caret {
          from, to { border-color: transparent }
          50% { border-color: #6366f1 }
        }

        .delay-100 {
          animation-delay: 0.1s;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }
      `}</style>
    </div>
  );
};

export default LoadingPassages;
